/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Auth Service - Authentication API
 * ============================================================================
 *
 * Service layer for all authentication-related API calls.
 * Endpoints aligned with Spring Boot backend.
 *
 * ============================================================================
 */

import { get, post } from './api/client';
import { ApiResponse } from './api/types';
import type { User } from '@/stores/authStore';
import {
  LoginRequest,
  AuthenticationResult,
  MfaVerifyRequest,
  MfaSetupResponse,
  MfaConfirmRequest,
  MfaConfirmResponse,
  TokenRefreshRequest,
  TokenPair,
  PasswordResetRequestPayload,
  PasswordResetRequestResponse,
  VerifyResetTokenRequest,
  PasswordResetValidateResponse,
  MfaForResetRequest,
  MfaForResetResponse,
  PasswordResetPayload,
  ChangePasswordRequest,
  ValidatePasswordRequest,
  ValidatePasswordResponse,
  RecoveryCodesResponse,
  VerifyForRecoveryRequest,
  BootstrapStatus,
  BootstrapRequest,
  BootstrapResult,
  MfaSetupVerifyIdentityRequest,
  MfaSetupVerifyIdentityResponse,
  MfaSetupInitTotpRequest,
  MfaSetupCompleteRequest,
  AcceptInviteRequest,
} from '@/types/auth.types';

const AUTH_BASE = '/auth';
const MFA_BASE = '/mfa';
const PASSWORD_BASE = '/password';
const BOOTSTRAP_BASE = '/bootstrap';

// ============================================================================
// AUTHENTICATION
// ============================================================================

/**
 * Login with email and password
 * @endpoint POST /api/v1/auth/login
 */
export async function login(data: LoginRequest): Promise<AuthenticationResult> {
  const response = await post<ApiResponse<AuthenticationResult>>(`${AUTH_BASE}/login`, data);
  return response.data;
}

/**
 * Verify MFA code after login
 * @endpoint POST /api/v1/auth/mfa/verify
 */
export async function verifyMfa(data: MfaVerifyRequest): Promise<AuthenticationResult> {
  const response = await post<ApiResponse<AuthenticationResult>>(`${AUTH_BASE}/mfa/verify`, data);
  return response.data;
}

/**
 * Refresh access token using refresh token
 * @endpoint POST /api/v1/auth/refresh
 */
export async function refreshToken(data: TokenRefreshRequest): Promise<TokenPair> {
  const response = await post<ApiResponse<TokenPair>>(`${AUTH_BASE}/refresh`, data);
  return response.data;
}

/**
 * Fetch the currently authenticated user.
 *
 * Unlike the login/MFA response — which is built on an unauthenticated request
 * and therefore cannot resolve day-scope — this authenticated call returns the
 * correct `currentDayRestricted` flag (and other server-resolved profile fields).
 * @endpoint GET /api/v1/auth/me
 */
export async function getCurrentUser(): Promise<User> {
  const response = await get<ApiResponse<User>>(`${AUTH_BASE}/me`);
  return response.data;
}

/**
 * Logout current session
 * @endpoint POST /api/v1/auth/logout
 */
export async function logout(): Promise<void> {
  await post<ApiResponse<void>>(`${AUTH_BASE}/logout`);
}

/**
 * Logout all sessions for current user
 * @endpoint POST /api/v1/auth/logout-all
 */
export async function logoutAll(): Promise<number> {
  const response = await post<ApiResponse<number>>(`${AUTH_BASE}/logout-all`);
  return response.data;
}

// ============================================================================
// MFA SETUP
// ============================================================================

/**
 * Initialize TOTP setup - generates QR code and secret
 * @endpoint POST /api/v1/mfa/setup/totp
 */
export async function initializeTotpSetup(): Promise<MfaSetupResponse> {
  const response = await post<ApiResponse<MfaSetupResponse>>(`${MFA_BASE}/setup/totp`);
  return response.data;
}

/**
 * Verify TOTP setup with initial code from authenticator app
 * @endpoint POST /api/v1/mfa/setup/totp/verify
 */
export async function verifyTotpSetup(data: MfaConfirmRequest): Promise<MfaConfirmResponse> {
  const response = await post<ApiResponse<MfaConfirmResponse>>(`${MFA_BASE}/setup/totp/verify`, data);
  return response.data;
}

/**
 * Send OTP code to user's email during MFA verification flow
 * @param mfaToken The MFA token
 * @param isSetup If true, skip cooldown timer (for initial MFA setup)
 * @endpoint POST /api/v1/auth/mfa/resend-email-otp
 */
export async function sendEmailOtp(mfaToken: string, isSetup = false): Promise<void> {
  const url = isSetup
    ? `${AUTH_BASE}/mfa/resend-email-otp?setup=true`
    : `${AUTH_BASE}/mfa/resend-email-otp`;
  await post<ApiResponse<void>>(url, { mfaToken });
}

/**
 * Regenerate recovery codes (invalidates old ones)
 * @endpoint POST /api/v1/mfa/recovery-codes
 */
export async function regenerateRecoveryCodes(data: VerifyForRecoveryRequest): Promise<RecoveryCodesResponse> {
  const response = await post<ApiResponse<RecoveryCodesResponse>>(`${MFA_BASE}/recovery-codes`, data);
  return response.data;
}

// ============================================================================
// MFA SETUP DURING LOGIN (for enforced MFA users)
// ============================================================================

/**
 * Verify identity with Email OTP before MFA setup
 * @endpoint POST /api/v1/auth/mfa/setup/verify-identity
 */
export async function verifyIdentityForMfaSetup(data: MfaSetupVerifyIdentityRequest): Promise<MfaSetupVerifyIdentityResponse> {
  const response = await post<ApiResponse<MfaSetupVerifyIdentityResponse>>(`${AUTH_BASE}/mfa/setup/verify-identity`, data);
  return response.data;
}

/**
 * Initialize TOTP during MFA setup flow (after identity verification)
 * @endpoint POST /api/v1/auth/mfa/setup/init-totp
 */
export async function initTotpForMfaSetup(data: MfaSetupInitTotpRequest): Promise<MfaSetupResponse> {
  const response = await post<ApiResponse<MfaSetupResponse>>(`${AUTH_BASE}/mfa/setup/init-totp`, data);
  return response.data;
}

/**
 * Complete MFA setup and get authentication tokens
 * @endpoint POST /api/v1/auth/mfa/setup/complete
 */
export async function completeMfaSetup(data: MfaSetupCompleteRequest): Promise<AuthenticationResult> {
  const response = await post<ApiResponse<AuthenticationResult>>(`${AUTH_BASE}/mfa/setup/complete`, data);
  return response.data;
}

// ============================================================================
// PASSWORD MANAGEMENT
// ============================================================================

/**
 * Request password reset email
 * @endpoint POST /api/v1/password/reset/request
 */
export async function requestPasswordReset(data: PasswordResetRequestPayload): Promise<PasswordResetRequestResponse> {
  const response = await post<ApiResponse<PasswordResetRequestResponse>>(`${PASSWORD_BASE}/reset/request`, data);
  return response.data;
}

/**
 * Verify password reset token
 * @endpoint POST /api/v1/password/reset/verify
 */
export async function verifyResetToken(data: VerifyResetTokenRequest): Promise<PasswordResetValidateResponse> {
  const response = await post<ApiResponse<PasswordResetValidateResponse>>(`${PASSWORD_BASE}/reset/verify`, data);
  return response.data;
}

/**
 * Verify MFA for password reset
 * @endpoint POST /api/v1/password/reset/mfa
 */
export async function verifyMfaForReset(data: MfaForResetRequest): Promise<MfaForResetResponse> {
  const response = await post<ApiResponse<MfaForResetResponse>>(`${PASSWORD_BASE}/reset/mfa`, data);
  return response.data;
}

/**
 * Complete password reset with new password
 * @endpoint POST /api/v1/password/reset/complete
 */
export async function completePasswordReset(data: PasswordResetPayload): Promise<void> {
  await post<ApiResponse<void>>(`${PASSWORD_BASE}/reset/complete`, data);
}

/**
 * Change password for logged-in user
 * @endpoint POST /api/v1/password/change
 */
export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  await post<ApiResponse<void>>(`${PASSWORD_BASE}/change`, data);
}

/**
 * Validate password strength
 * @endpoint POST /api/v1/password/validate
 */
export async function validatePassword(data: ValidatePasswordRequest): Promise<ValidatePasswordResponse> {
  const response = await post<ApiResponse<ValidatePasswordResponse>>(`${PASSWORD_BASE}/validate`, data);
  return response.data;
}

// ============================================================================
// BOOTSTRAP
// ============================================================================

/**
 * Get bootstrap status - check if system needs initial setup
 * @endpoint GET /api/v1/bootstrap/status
 */
export async function getBootstrapStatus(): Promise<BootstrapStatus> {
  const response = await get<ApiResponse<BootstrapStatus>>(`${BOOTSTRAP_BASE}/status`);
  return response.data;
}

/**
 * Bootstrap the first SuperAdmin
 * @endpoint POST /api/v1/bootstrap
 */
export async function bootstrap(data: BootstrapRequest): Promise<BootstrapResult> {
  const response = await post<ApiResponse<BootstrapResult>>(BOOTSTRAP_BASE, data);
  return response.data;
}

// ============================================================================
// ACCEPT INVITATION
// ============================================================================

/**
 * Accept organization invitation by setting a new password
 * @endpoint POST /api/v1/auth/accept-invite
 */
export async function acceptInvitation(data: AcceptInviteRequest): Promise<AuthenticationResult> {
  const response = await post<ApiResponse<AuthenticationResult>>(`${AUTH_BASE}/accept-invite`, data);
  return response.data;
}

// ============================================================================
// EXPORT ALL
// ============================================================================

export const authService = {
  // Auth
  login,
  verifyMfa,
  refreshToken,
  logout,
  logoutAll,
  // MFA Setup (authenticated user)
  initializeTotpSetup,
  verifyTotpSetup,
  sendEmailOtp,
  regenerateRecoveryCodes,
  // MFA Setup (during login - for enforced MFA)
  verifyIdentityForMfaSetup,
  initTotpForMfaSetup,
  completeMfaSetup,
  // Password
  requestPasswordReset,
  verifyResetToken,
  verifyMfaForReset,
  completePasswordReset,
  changePassword,
  validatePassword,
  // Bootstrap
  getBootstrapStatus,
  bootstrap,
  // Invitation
  acceptInvitation,
};

export default authService;
